import { ComponentType } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  Signal,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WidgetChangeDetectionObject } from '@api-response-types/forecast-series.response';
import {
  WidgetPortfolioTotals,
  WidgetPortfolioTotalsChartData,
  WidgetPortfolioTotalsSeries,
} from '@api-types/dtos/widget-portfolio-totals.dto';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { getFactorColumn, TOTALS_NAME } from '@api-types/helper/helper';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  H3Component,
  PmButtonComponent,
  PmTooltipDirective,
} from '@libs/ui';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { selectAvailableFilters } from 'demand-forecasting/store/filter/filter.selectors';
import {
  latestCompletedForecastSeries,
  selectForecastSeriesPossibleYears,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { LucideScan } from '@lucide/angular';
import { catchError, map, throwError } from 'rxjs';
import { DefaultSelectionPortfolioTotalsMultiYearWidget } from '../../types/types';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'pm-portfolio-totals-multi-year-widget',
  templateUrl: './portfolio-totals-multi-year-widget.component.html',
  imports: [
    WidgetBaseComponent,
    WidgetTitleDirective,
    PmTooltipDirective,
    WidgetHeaderDirective,
    WidgetContentDirective,
    HighchartsChartComponent,
    WidgetActionsDirective,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    H3Component,
    PmButtonComponent,
    FormFieldComponent,
    FormInputSelectComponent,
  ],
})
export class PortfolioTotalsMultiYearWidgetComponent extends WidgetBaseComponent<PortfolioTotalsMultiYearWidgetComponent> {
  @Input({
    required: false,
  })
  targetUnit: string | undefined;

  @Input({
    required: false,
  })
  filter?: string;

  @Input({
    required: false,
  })
  years?: number[] | number;

  private readonly store = inject(Store);

  /**
   * Needed cause of a bug in the edit filter cause years
   * have been saved as number instead number array
   */
  private normalizeYears(years: number[] | number | undefined): number[] {
    if (years === undefined) {
      return [];
    }
    return Array.isArray(years) ? years : [years];
  }
  private readonly insightsHttpClient: InsightsHttpClient =
    inject(InsightsHttpClient);

  protected readonly scanIcon = LucideScan;

  protected readonly MAX_NUMBER_OF_SELECTIONS = 5;
  protected readonly MAX_NUMBER_OF_CATEGORUIES = 5;

  private readonly configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  private readonly factorColumn = computed(() => {
    const configuration = this.configuration();
    if (!configuration) {
      return undefined;
    }
    return this.targetUnit
      ? getFactorColumn(configuration, this.targetUnit)
      : undefined;
  });

  selectedYears: number[] = [];

  dataSeriesByFilterMultiYear = signal<WidgetPortfolioTotals>({});
  currentGroupName = '';
  currentYears: number[] = [];
  filterNames: string[] = [];

  protected chartOptions = signal<HighchartsOptions>(
    BaseChartOptions.getEmptyChartOptions(),
  );
  chart?: Highcharts.Chart;
  chartUpdated = false;

  destroyRef = inject(DestroyRef);

  isLoading = true;
  isError = false;

  private readonly forecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );
  private readonly forecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  factorColumn$ = this.store.select(
    ConfigurationsSelectors.selectFactorColumnByUrlParam,
  );
  possibleYears = this.store.selectSignal(selectForecastSeriesPossibleYears);

  private readonly availableFilters = this.store.selectSignal(
    selectAvailableFilters,
  );

  previousPortfolioTotalsChangeDetectionObject: WidgetChangeDetectionObject;

  groupsSelectOptions: Signal<NgxVirtualSelectFieldOptionModel<string>[]> =
    computed(() => {
      const groups = Object.keys(this.dataSeriesByFilterMultiYear());
      return groups.map((group) => ({
        value: group,
        label: group,
        disabled: false,
      }));
    });

  yearsSelectOptions: Signal<NgxVirtualSelectFieldOptionModel<number>[]> =
    computed(() =>
      this.possibleYears().map((year) => ({
        value: year,
        label: String(year),
        disabled: false,
      })),
    );

  public data: DefaultSelectionPortfolioTotalsMultiYearWidget = inject(
    MAT_DIALOG_DATA,
    { optional: true },
  );

  constructor() {
    super();
    if (this.data) {
      this.targetUnit = this.data.targetUnit;
      this.filter = this.data.filter;
      this.years = this.data.years?.map((year: string) => +year);
    }
    effect(() => {
      const forecastSeries = this.forecastSeries();
      const forecastVersion = this.forecastVersion();

      if (
        !forecastSeries ||
        !forecastVersion ||
        forecastVersion.status !== ForecastVersionStatus.FINISHED
      ) {
        return;
      }

      const newSeriesChangeDetectionObject: WidgetChangeDetectionObject = {
        _resultSetStatus: forecastSeries._resultSetStatus,
        configurationId: forecastSeries.configurationId,
        resultSetId: forecastVersion.id, // TODO forecastSeries.resultSetId seems to never get updated
        yAxis: forecastSeries.yAxis,
      };

      if (
        JSON.stringify(newSeriesChangeDetectionObject) ===
        JSON.stringify(this.previousPortfolioTotalsChangeDetectionObject ?? {})
      ) {
        return;
      }

      this.previousPortfolioTotalsChangeDetectionObject =
        newSeriesChangeDetectionObject;

      const possibleYears = this.possibleYears();
      const normalizedYears = this.normalizeYears(this.years);
      this.currentYears =
        normalizedYears ??
        possibleYears.slice(
          possibleYears.length - this.MAX_NUMBER_OF_SELECTIONS + 1,
          possibleYears.length,
        );
      this.setDefaultFilter();

      this.fetchData();
    });
  }

  setDefaultFilter() {
    this.selectedYears = this.currentYears;
    this.currentGroupName = this.filter || this.filterNames[0] || TOTALS_NAME;
  }

  private fetchData() {
    const configuration = this.configuration();
    const forecastSeries = this.forecastSeries();
    const forecastVersion = this.forecastVersion();
    if (!forecastSeries || !forecastVersion || !configuration) {
      console.error('No forecast series or version found');
      return;
    }
    this.isLoading = true;
    this.isError = false;
    this.insightsHttpClient
      .getPortfolioTotalsMultiYear(
        forecastVersion.forecastId,
        this.factorColumn(),
        undefined,
        this.selectedYears.map((year) => year.toString()),
        configuration.startForecastConfig.resolution,
      )
      .pipe(
        map((res) => {
          this.isLoading = false;
          if (!this.forecastSeries) {
            console.error('No forecast series found');
            return;
          }
          this.dataSeriesByFilterMultiYear.set(res);
          this.filterNames = this.availableFilters().map(
            (filterObj) => filterObj.column,
          );

          this.currentGroupName = this.filter
            ? this.filter
            : this.filterNames[0] || TOTALS_NAME;
          this.setupChart();
          this.isLoading = false;
        }),
        catchError((error) => {
          this.isLoading = false;
          this.isError = true;
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  setChartOptions(data: WidgetPortfolioTotalsChartData) {
    const nonZeroSeries = data.series.filter(
      (series: WidgetPortfolioTotalsSeries) =>
        series.data.some((value: number) => value !== 0),
    );

    const chartOptions = new ChartOptionsBuilder()
      .withPlotOptions({
        bar: {
          stacking: 'normal',
        },
      })
      .withXAxis({
        title: {
          text: this.forecastSeries()?.xAxis || 'date',
        },
        categories: data.categories,
      })
      .withYAxis({
        title: {
          text: this.targetUnit || this.forecastSeries()?.yAxis || 'value',
        },
        stackLabels: {
          enabled: true,
          formatter() {
            return Highcharts.numberFormat(this.total, 2);
          },
        },
      })
      .withSeries(
        nonZeroSeries.map((series: WidgetPortfolioTotalsSeries) => ({
          type: 'bar',
          data: series.data,
          name: series.name,
          stack: series.stack,
          marker: { enabled: false },
        })),
      )
      .withTooltip({
        shared: false,
        useHTML: true,
        formatter() {
          // Extract the year from the series name
          const year = this.series.name.split(' ')[0];
          const { y } = this;
          if (!year || !y) {
            return '';
          }
          return `<b>${this.x}</b><br/>${year}: ${Highcharts.numberFormat(y, 2)}`;
        },
      })
      .build();

    this.chartOptions.set(chartOptions);
  }
  findNthOccurrenceIndex(array: number[], value: number, n: number): number {
    if (n <= 0) return -1;

    let occurrenceCount = 0;

    for (const [index, element] of array.entries()) {
      if (element === value) {
        occurrenceCount += 1;
        if (occurrenceCount === n) {
          return index;
        }
      }
    }
    return -1;
  }

  setupChart() {
    // Deep copy dataSeriesByYear to avoid changing the original object
    const dataSeriesMultiYear = this.dataSeriesByFilterMultiYear();
    const dataSeriesByFilterMultiYearCopy =
      structuredClone(dataSeriesMultiYear);
    const currentDataSeries =
      dataSeriesByFilterMultiYearCopy[this.currentGroupName];
    const currCopy = structuredClone(currentDataSeries);

    // TODO remove this spaghetti code and directly to stuff like this in the backend with proper function names
    // --------------------------- Sorting and Splicing ---------------------------

    // We're only interested in the last two entries -> e.g. 2024 Actuals and 2024 Predictions
    // Sorting below requires both entries, so bail out otherwise
    const seriesCount = currCopy.series?.length ?? 0;
    if (seriesCount < 2) {
      this.setChartOptions(currCopy);
      return;
    }
    const first = currCopy.series[seriesCount - 1].data;
    const second = currCopy.series[seriesCount - 2].data;
    // Sum up the values of the first and second array at the same index so we get totals (actuals + predictions)
    const sumsOfFirstAndSecondWithSameIndex = first.map(
      (value: number, index: number) => value + second[index],
    );

    // Set up an indexCategoryMap to save the initial index of each category
    const indexCategoryMap: { [key: number]: string } = {};
    currCopy.categories.forEach((category: string, index: number) => {
      indexCategoryMap[index] = category;
    });
    // Sort sumsOfFirstAndSecondWithSameIndex
    const sortedSums = [...sumsOfFirstAndSecondWithSameIndex].sort(
      (a: number, b: number) => b - a,
    );
    // After sorting, check which previous index is now at which new index
    const indexMapping: { [key: string]: number } = {};
    const rememberDuplicates: { [key: number]: number } = {};
    sortedSums.forEach((value: number, index: number) => {
      // Check if we have duplicates and if so, we need to access the nth occurence of that exact value in the sumsOfFirstAndSecondWithSameIndex object to determine the original position of that value
      const accessIndexForMapping = rememberDuplicates[value]
        ? this.findNthOccurrenceIndex(
            sumsOfFirstAndSecondWithSameIndex,
            value,
            rememberDuplicates[value],
          )
        : sumsOfFirstAndSecondWithSameIndex.indexOf(value);
      indexMapping[accessIndexForMapping] = index;
      rememberDuplicates[value] = rememberDuplicates[value]
        ? rememberDuplicates[value] + 1
        : 2;
    });
    // Now after sorting and mapping we know e.g. index 0 needs to map onto index 2
    // Execute the mapping on the categories array first
    currCopy.categories = currCopy.categories.map(
      (category: string, index: number) => {
        // Check which index we need to access the categories object
        const accessIndex = Object.keys(indexMapping).findIndex(
          (key: string) => indexMapping[key] === index,
        );
        if (accessIndex === undefined) {
          return category;
        }
        return currCopy.categories[+accessIndex];
      },
    );

    // Secondly on the data array inside of each series
    currCopy.series = currCopy.series.map((series) => {
      series.data = series.data.map((value: number, index: number) => {
        const accessIndex = Object.keys(indexMapping).findIndex(
          (key: string) => indexMapping[key] === index,
        );
        if (accessIndex === undefined) {
          return value;
        }
        return series.data[+accessIndex];
      });

      return series;
    });

    // Slice to only show the top MAX_SHOW_CATEGORIES categories if not dialog
    if (!this.isDialogInstance) {
      currCopy.series.forEach((series) => {
        series.data = series.data.slice(0, this.MAX_NUMBER_OF_CATEGORUIES);
      });
      currCopy.categories = currCopy.categories.slice(
        0,
        this.MAX_NUMBER_OF_CATEGORUIES,
      );
    }
    this.setChartOptions(currCopy);
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  onGroupChange(value: string | string[]): void {
    // this is no multiselect - therefore the value is always a string
    if (Array.isArray(value)) {
      return;
    }

    this.currentGroupName = value;
    this.setupChart();
  }

  async onYearChange(value: number[] | number) {
    if (!Array.isArray(value) || isEqual(value, this.selectedYears)) {
      // we know its an array cause it is a multi select -- therefore ignore the other cases
      return;
    }

    this.selectedYears = [...value].sort((a: number, b: number) => a - b);

    const isDeselect = this.currentYears.length === value.length + 1;
    const deselectedYear = this.currentYears.find(
      (year) => !value.includes(year),
    );
    this.currentYears = value;

    if (isDeselect && deselectedYear) {
      this.deselectYear(deselectedYear);
      return;
    }
    this.fetchData();
  }

  deselectYear(year: number) {
    const forecastSeries = this.forecastSeries();
    if (!forecastSeries) {
      return;
    }
    const dataSeriesCopy: WidgetPortfolioTotals = structuredClone(
      this.dataSeriesByFilterMultiYear(),
    );
    Object.keys(dataSeriesCopy).forEach((key) => {
      dataSeriesCopy[key].series = dataSeriesCopy[key].series.filter(
        (series: WidgetPortfolioTotalsSeries) =>
          !series.name.includes(year.toString()),
      );
    });
    this.dataSeriesByFilterMultiYear.set(dataSeriesCopy);
    this.setupChart();
  }

  // Override the showFullScreen method from the WidgetBaseComponent to handle afterClosed event properly
  showFullScreen(
    componentType: ComponentType<PortfolioTotalsMultiYearWidgetComponent>,
    filterSelection?: DefaultSelectionPortfolioTotalsMultiYearWidget,
  ): MatDialogRef<PortfolioTotalsMultiYearWidgetComponent> | null {
    const dialog = super.showFullScreen(componentType, filterSelection);
    dialog?.afterClosed().subscribe(() => {
      const dialogGroupName = dialog.componentInstance.currentGroupName;
      const dialogYears = dialog.componentInstance.currentYearsAsStringArray;

      this.currentYears = dialogYears.map((year: string) => +year);
      this.selectedYears = this.currentYears;
      this.filter = dialogGroupName;
      this.fetchData();
    });
    return null;
  }

  get PortfolioTotalsMultiYearWidgetComponent(): typeof PortfolioTotalsMultiYearWidgetComponent {
    return PortfolioTotalsMultiYearWidgetComponent;
  }

  get currentYearsAsStringArray(): string[] {
    return this.currentYears?.map((year) => year.toString()) || [];
  }
}
